import React, { useMemo, useRef, useState } from 'react'
import moment from 'moment'
import CircularProgress from '@material-ui/core/CircularProgress'
import ListTable from 'Components/ListTable'
import { MultiSelectColumnFilter } from 'filters'
import useTranslation from 'Hooks/useTranslation'
import { formatFleetValue } from '../fleetLiveHelpers'
import { DETAIL_BLOCKS } from '../../Users/Blocks/fleetViewSettingsStorage'
import { getEventTypeName, mapPumpUnitToCard } from './pumpCardHelpers'
import PumpStatusTooltip from './PumpStatusTooltip'

const TAB_CHANNELS = 'channels'
const TAB_EVENTS = 'events'

const CATEGORY_TO_BLOCK_KEY = {
  engine: 'engine',
  transmission: 'transmission',
  pump: 'pump',
  hydraulic: 'hydraulic',
  dgb: 'dgb',
}

const normalizeUnit = (unit) => {
  if (unit == null || unit === '' || unit === '-') return ''
  return String(unit)
}

const renderFieldValue = (field) => {
  const value = field?.value
  if (value == null || value === '') return '—'
  const unit = normalizeUnit(field.unit)
  const formatted = formatFleetValue(value, field.decimals != null ? field.decimals : 1)
  if (formatted === '—') return '—'
  return unit ? `${formatted} ${unit}` : formatted
}

const buildSectionsFromFields = (unit) => {
  const fields = Array.isArray(unit?.fields) ? unit.fields : []
  const byBlock = DETAIL_BLOCKS.reduce((acc, block) => {
    acc[block.key] = []
    return acc
  }, {})

  fields
    .filter((field) => field?.isDetailField && field.fieldName)
    .forEach((field) => {
      const blockKey = CATEGORY_TO_BLOCK_KEY[String(field.category || '').toLowerCase()]
      if (!blockKey || !byBlock[blockKey]) return
      byBlock[blockKey].push(field)
    })

  return DETAIL_BLOCKS.map((block) => ({
    ...block,
    fields: [...byBlock[block.key]].sort((a, b) => (a?.order ?? 0) - (b?.order ?? 0)),
  }))
}

const eventRowKey = (event) => `${event.id}:${event.eventState}:${event.text}`

/**
 * Detail data is re-sent every tick; the events list keeps its identity until a row changes,
 * so the table does not jump back to the first page on every update.
 */
const useStableEvents = (events) => {
  const ref = useRef({ key: '', events: [] })
  const list = Array.isArray(events) ? events : []
  const key = list.map(eventRowKey).join('|')
  if (key !== ref.current.key) {
    ref.current = {
      key,
      events: list.map((event) => ({ ...event, type: getEventTypeName(event.type) })),
    }
  }
  return ref.current.events
}

const PumpEventsTable = ({ events, translation }) => {
  const columns = useMemo(() => [
    {
      Header: translation.fleetEventsTimeLabel || 'Time',
      accessor: 'timestamp',
      Cell: ({ value }) => moment(value).local().format('M/D/YYYY h:mm:ss A'),
    },
    {
      Header: translation.fleetEventsTypeLabel || 'Event Type',
      accessor: 'type',
      Filter: MultiSelectColumnFilter,
      filter: 'includesSome',
      Cell: ({ value }) => (
        <span className={`fdEventType fdEventType--${String(value || '').toLowerCase()}`}>{value}</span>
      ),
    },
    {
      Header: translation.fleetEventsMessageLabel || 'Message',
      accessor: 'text',
      canFilter: false,
      Cell: ({ value }) => String(value || '').split('\r\n').map((row, index) => (
        <div style={{ whiteSpace: 'normal' }} key={index}>{row}</div>
      )),
      className: 'message-column',
    },
    {
      Header: translation.fleetEventsSenderLabel || 'Sender',
      accessor: 'senderName',
      canFilter: false,
    },
    {
      Header: translation.fleetEventsMessageIdLabel || 'Message ID',
      accessor: 'senderMessageId',
      canFilter: false,
    },
    {
      Header: translation.fleetEventsStateLabel || 'Event State',
      accessor: 'isActive',
      canFilter: false,
      Cell: ({ value }) => (
        <span className={value ? 'fdEventState fdEventState--active' : 'fdEventState'}>
          {value
            ? (translation.fleetEventsStateActiveLabel || 'Active')
            : (translation.fleetEventsStateInactiveLabel || 'Inactive')}
        </span>
      ),
    },
  ], [translation])

  return (
    <ListTable
      emptyDataText={<span>{translation.fleetEventsEmptyLabel || 'No events found.'}</span>}
      initSort={[{ id: 'timestamp', desc: true }]}
      columns={columns}
      enableColumnFilters={true}
      data={events}
      tableClass="bordered"
    />
  )
}

const PumpDetailPanel = ({
  unit,
  isLoading = false,
  onClose,
}) => {
  const translation = useTranslation()
  const [tab, setTab] = useState(TAB_CHANNELS)
  const sections = useMemo(() => buildSectionsFromFields(unit), [unit])
  const events = useStableEvents(unit?.events)
  const card = unit?.unitUId ? mapPumpUnitToCard(unit) : null

  if (!unit && !isLoading) return null
  const mainSections = sections.filter((s) => s.key !== 'dgb')
  const dgbSection = sections.find((s) => s.key === 'dgb')

  return (
    <div className="fdModalScrim fdPumpModalScrim" onClick={onClose}>
      <div
        className="fdModal fdPumpModal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="fdModalHead fdPumpModalHead">
          <div className="fdPumpModalHeadMain">
            {card && !isLoading ? (
              <PumpStatusTooltip unit={unit} disabled={!card.selectable}>
                <span
                  className={`fdDot fdPumpModalStatus ${card.pulse ? 'fdDot--pulse' : ''}`}
                  style={{ background: card.statusColor, '--fd-active': card.statusColor }}
                />
              </PumpStatusTooltip>
            ) : null}
            <span className="fdPumpModalId">{unit?.unitName || unit?.unitUId || '…'}</span>
            <span className="fdModalSub fdPumpModalLicense">
              {unit?.unitUId || ''}
            </span>
          </div>
          <div className="fdPumpModalHeadActions">
            <div className={`optionsSwitch fdPumpModalTabs${tab === TAB_EVENTS ? ' in' : ''}`}>
              <div className="option" role="button" tabIndex={0} onClick={() => setTab(TAB_CHANNELS)}>
                {translation.fleetPumpChannelsTabLabel || 'Channels'}
              </div>
              <div className="option" role="button" tabIndex={0} onClick={() => setTab(TAB_EVENTS)}>
                {translation.fleetPumpEventsTabLabel || 'Events'}
              </div>
            </div>
            <button type="button" className="fdModalClose" onClick={onClose}>×</button>
          </div>
        </div>
        <div className="fdModalBody">
          {isLoading ? (
            <div className="fdPumpModalLoading">
              <CircularProgress size={36} style={{ color: 'rgba(0, 0, 0, 0.45)' }} />
            </div>
          ) : tab === TAB_EVENTS ? (
            events.length ? (
              <PumpEventsTable events={events} translation={translation} />
            ) : (
              <div className="fdPumpModalEmpty">
                {translation.fleetEventsEmptyLabel || 'No events found.'}
              </div>
            )
          ) : (
            <>
              <div className="fdPumpModalGroups">
                {mainSections.map((section) => (
                  <div key={section.key} className="fdGroupCard">
                    <div className="fdGroupCardTitle">
                      {section.title}
                    </div>
                    {section.fields.map((field) => (
                      <div key={`${field.fieldName}-${field.order}`} className="fdGroupCardRow">
                        <span>{field.fieldName}</span>
                        <b>{renderFieldValue(field)}</b>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
              {dgbSection ? (
                <div className="fdDgb">
                  <div className="fdGroupCardTitle">
                    {dgbSection.title}
                  </div>
                  <div className="fdDgbGrid">
                    {dgbSection.fields.map((field) => (
                      <div key={`${field.fieldName}-${field.order}`} className="fdGroupCardRow">
                        <span>{field.fieldName}</span>
                        <b>{renderFieldValue(field)}</b>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default PumpDetailPanel
